import { EditableProps } from "@builder/components/editable/editableProps";
import { ActiveCompany } from "@common/build/activeEntityType";
import { QueryDataTables, QueryFieldsProducts, QueryOrderByOptionsProducts } from "@common/build/constants";
import {
    BaseLayerTypes,
    BuildComponentKey,
    BuildNode,
    BuildTypes,
    ButtonNode,
    CashLedgerNode,
    CustomerNotesNode,
    CustomNumpadNode,
    DynamicGridNode,
    EOSNode,
    FilterNode,
    GlobalBlockTypes,
    IconNode,
    MenuNode,
    NodeSizes,
    NodeStates,
    PaymentButtonNode,
    PopupAlignment,
    PopupNode,
    PrintButtonNode,
    ProductQuantityNode,
    ProductVariationsNode,
    ReceiptNode,
    SearchFieldNode,
    SearchNode,
    SlideoutNode,
    SmartGridNode,
    SmartTileTypes,
    TextNode,
    VismaButtonListNode
} from "@common/build/types";
import { v4 as uuidv4 } from "uuid";
import { deepCopy } from "./builderHelpers";

//Presets
import orderListPreset from "./presets/BasicOrderList.json";
import BasicProductGridPreset from "./presets/BasicProductGrid.json";
import BasicProductListPreset from "./presets/BasicProductList.json";
import customerNotesPreset from "./presets/CustomerNotes.json";
import customSalePreset from "./presets/CustomSale.json";
import emptyFixedCartPreset from "./presets/EmptyFixedCart.json";
import emptyUnfixedCartPreset from "./presets/EmptyUnfixedCart.json";
import noVariationsPreset from "./presets/noVariations.json";
import orderDataPreset from "./presets/orderData.json";
import BasicCustomerListPreset from "./presets/BasicCustomerList.json";
import vismaElementPreset from "./presets/vismaElement.json";
import SmartGridPreset from "./presets/SmartGrid.json";
import { ExtensionGUID } from "@builder/types/Extension.type";
import { InstalledBuilderElement } from "../builderElementsInstalledApiSlice";

export interface EditablePropsExt extends EditableProps {
    key: string;
}

export type NodeEdit = {
    editor?: { keys: EditablePropsExt[]; exclude?: (node: BuildNode, comparator?: any) => boolean };
    style?: { keys: EditablePropsExt[]; exclude?: (node: BuildNode, comparator?: any) => boolean };
    advanced?: { keys: EditablePropsExt[]; exclude?: (node: BuildNode, comparator?: any) => boolean };
};

export type BuilderUI = {
    key: BuildComponentKey;
    label: string;
    icon: string;
    excludedChildren: string[];
    restrictToParents?: BuildComponentKey[]; //restrict to only these parents (non direct parents)
    group: string;
    edit: NodeEdit;
    exclude?: boolean;
    states?: NodeStates[];
    extension?: ExtensionGUID;
    dynamicElement?: InstalledBuilderElement;
};

export interface RenderBuildNode extends BuildNode {
    preset?: BuildNode[];
    noEdit?: boolean;
}

type BuilderMap = {
    [key in BuildComponentKey]: {
        ui: BuilderUI;
        render: RenderBuildNode;
    };
};

const builderMap: BuilderMap = {
    //Page Elements
    baseLayerPage: {
        ui: {
            key: "baseLayerPage",
            label: "Base Layer - Page",
            icon: "/baselayer-icon.svg",
            excludedChildren: ["receiptData", "cashLedgerData", "eosData"],
            group: "",
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: ["margin"] }
                    ]
                }
                // advanced: []
            }
        },
        render: {
            id: "",
            name: "baseLayerPage",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: {
                width: NodeSizes.SCROLL,
                height: NodeSizes.SCROLL
            }
        }
    },
    baseLayerPopup: {
        ui: {
            key: "baseLayerPopup",
            label: "Base Layer - Popup",
            icon: "/baselayer-icon.svg",
            excludedChildren: [],
            group: "",
            edit: {
                editor: {
                    keys: [
                        { key: "backdrop", excluded: [] },
                        {
                            key: "editIcon",
                            title: "Close Button Icon",
                            path: ["closeButton", "icon"],
                            condition: (node: any) => !!node?.closeButton?.active
                        }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: ["fill"] },
                        { key: "spacing", excluded: ["margin"] },
                        {
                            key: "styleIcon",
                            path: ["closeButton", "icon", "style"],
                            condition: (node: any) => !!node.closeButton.icon.src && !!node.closeButton.active
                        },
                        {
                            key: "background",
                            path: ["closeButton", "style"],
                            title: "Close Button Background",
                            excluded: [],
                            condition: (node: any) => !!node.closeButton.active
                        },
                        {
                            key: "spacing",
                            path: ["closeButton", "style"],
                            title: "Close Button Spacing",
                            excluded: ["margin"],
                            condition: (node: any) => !!node.closeButton.active
                        },
                        {
                            key: "borders",
                            path: ["closeButton", "style"],
                            title: "Close Button Borders",
                            condition: (node: any) => !!node.closeButton.active
                        }
                    ]
                }
            }
        },
        render: {
            id: "",
            name: "baseLayerPopup",
            type: "component",
            backdropClose: true,
            alignment: PopupAlignment.CENTER,
            attr: {
                style: {
                    backgroundColor: "#ffffff",
                    flexDirection: "column"
                }
            },
            nodeSize: {
                width: NodeSizes.CUSTOM,
                height: NodeSizes.CUSTOM
            },
            closeButton: {
                active: true,
                icon: {
                    src: "cross"
                }
            }
        } as PopupNode
    },
    baseLayerCFD: {
        ui: {
            key: "baseLayerCFD",
            label: "Base Layer - Customer Display",
            icon: "/baselayer-icon.svg",
            excludedChildren: ["receiptData", "cashLedgerData", "eosData"],
            group: "",
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: ["margin"] }
                    ]
                }
            }
        },
        render: {
            id: "",
            name: "baseLayerCFD",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: {
                width: NodeSizes.SCROLL,
                height: NodeSizes.SCROLL
            }
        }
    },
    globalBlockPrint: {
        ui: {
            key: "globalBlockPrint",
            label: "Global Block - Print",
            icon: "/print-button-icon.svg",
            excludedChildren: [],
            group: "",
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "spacing", excluded: ["margin"] }
                    ]
                }
            }
        },
        render: {
            id: "",
            name: "globalBlockPrint",
            type: "component",
            attr: {
                style: {
                    fontFamily: "inherit",
                    flexDirection: "column",
                    alignItems: "center"
                }
            }
        }
    },
    globalBlockCFD: {
        ui: {
            key: "globalBlockCFD",
            label: "Global Block - CFD",
            icon: "/baselayer-icon.svg",
            excludedChildren: [],
            group: "",
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: ["margin"] }
                    ]
                }
            }
        },
        render: {
            id: "",
            name: "globalBlockCFD",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            }
        }
    },

    //Design Elements
    div: {
        ui: {
            key: "div",
            label: "Div container",
            icon: "/div-icon.svg",
            excludedChildren: [],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [{ key: "editAction", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "backgroundImage" },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "div",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.CUSTOM }
        }
    },
    repeaterDiv: {
        ui: {
            key: "repeaterDiv",
            label: "Repeater div",
            icon: "/repeater-div-icon.svg",
            excludedChildren: [],
            group: "Design Elements",
            exclude: true,
            edit: {
                editor: {
                    keys: [{ key: "editAction", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: ["width"] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.SELECTED, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "repeaterDiv",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    },
    templateDiv: {
        ui: {
            key: "templateDiv",
            label: "Template Div",
            icon: "/template-div-icon.svg",
            excludedChildren: [],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [{ key: "templateDiv", include: ["name", "image"] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "backgroundImage" },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "templateDiv",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FIT }
        }
    },
    text: {
        ui: {
            key: "text",
            label: "Text",
            icon: "/text-icon.svg",
            excludedChildren: ["*"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [{ key: "editText", excluded: [] }, { key: "editAction" }]
                },
                style: {
                    keys: [
                        { key: "size", excluded: ["height"] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: TextNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: TextNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: TextNode) => !!node?.fallback?.value
                        },
                        { key: "spacing", excluded: ["padding"] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "text",
            type: "component",
            text: "This is a basic text element.",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        }
    },
    button: {
        ui: {
            key: "button",
            label: "Button",
            icon: "/button-icon.svg",
            excludedChildren: ["*"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [{ key: "button", excluded: [] }, { key: "editAction" }, { key: "editIcon", excluded: ["actions"] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "button",
            type: "component",
            text: "Click me",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        }
    },
    icon: {
        ui: {
            key: "icon",
            label: "Icon",
            icon: "/icon-icon.svg",
            excludedChildren: ["*"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editIcon", excluded: [], path: ["icon"] },
                        { key: "editAction", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "styleIcon", excluded: [] },
                        { key: "background" },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "hideShow", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "icon",
            type: "component",
            icon: {
                src: "Circle-User"
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as IconNode
    },
    image: {
        ui: {
            key: "image",
            label: "Image",
            icon: "/image-icon.svg",
            excludedChildren: ["*"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editImage", excluded: [] },
                        { key: "editAction", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "imageStyle", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "image",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    },

    //Main POS Elements
    smartGrid: {
        ui: {
            key: "smartGrid",
            label: "Smart Grid",
            icon: "/smart-grid-icon.svg",
            group: "Main POS Elements",
            excludedChildren: ["*"],
            edit: {
                editor: {
                    keys: [
                        { key: "editSmartGrid", excluded: [] },
                        { key: "button", path: ["button", "text"], excluded: ["dynamic"] },
                        { key: "editIcon", path: ["button", "icon"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "styleSmartGrid" },
                        { key: "layout", excluded: ["flexDirection", "justifyContent"] },
                        { key: "layout", title: "Button Layout", path: ["button", "style"] },
                        { key: "size", excluded: [NodeSizes.FIT, NodeSizes.SCROLL] },
                        { key: "size", title: "Button Size", path: ["button", "style"] },
                        { key: "typography", title: "Button Typography", path: ["button", "style"] },
                        { key: "spacing" },
                        { key: "spacing", title: "Button Spacing", path: ["button", "style"], excluded: ["margin"] },
                        { key: "background" },
                        { key: "background", title: "Button Background", path: ["button", "style"] },
                        { key: "borders" },
                        { key: "borders", title: "Button Borders", path: ["button", "style"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            }
        },
        render: {
            id: "",
            name: "smartGrid",
            type: "component",
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL },
            smartGrid: {
                columns: 4,
                rows: 3,
                allowedTypes: Object.values(SmartTileTypes),
                tileStyles: {},
                style: {
                    columnGap: "20px",
                    rowGap: "20px"
                }
            },
            button: {
                style: {
                    backgroundColor: "#2797e8",
                    color: "#ffffff",
                    borderRadius: "8px",
                    width: "100%",
                    height: "44px",
                    justifyContent: "center",
                    alignItems: "center",
                    flexDirection: "row"
                }
            },
            attr: {
                style: {
                    columnGap: "20px",
                    rowGap: "20px",
                    padding: "20px"
                }
            },
            preset: SmartGridPreset
        } as SmartGridNode
    },
    smartTile: {
        ui: {
            key: "smartTile",
            label: "Smart Tile",
            icon: "/smart-grid-icon.svg",
            excludedChildren: [],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editFolderTile", condition: (node, comparator) => node.id === comparator.id && comparator.index === 2 },
                        { key: "editActionTile", condition: (node, comparator) => node.id === comparator.id && comparator.index === 3 },
                        { key: "editProductTile", condition: (node, comparator) => node.id === comparator.id && comparator.index === 5 },
                        { key: "editCategoryTile", condition: (node, comparator) => node.id === comparator.id && comparator.index === 4 }
                    ],
                    exclude: (_: BuildNode, comparator: any) => !comparator.id || comparator.index === 0 || comparator.index === 1
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "backgroundImage" },
                        // { key: "size", excluded: ["width"] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                    // { key: "conditions", excluded: [] },
                    // { key: "dynamicDataOptions", excluded: [] }
                }
            }
        },
        render: {
            id: "",
            name: "smartTile",
            type: "component",
            conditionType: SmartTileTypes.EMPTY,
            attr: {
                style: {
                    width: "100%",
                    height: "100%",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            noCopy: true,
            noMove: true,
            noDelete: true
        }
    },
    search: {
        ui: {
            key: "search",
            label: "Search",
            icon: "/builder-search-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "search", excluded: [] },
                        { key: "editIcon", excluded: ["actions"], path: ["icons", "search"], title: "Search Icon" },
                        { key: "editIcon", excluded: ["actions"], path: ["icons", "products"], title: "Product Icon" },
                        { key: "editIcon", excluded: ["actions"], path: ["icons", "customers"], title: "Customer Icon" },
                        { key: "editIcon", excluded: ["actions"], path: ["icons", "orders"], title: "Order Icon" }
                    ]
                },
                style: {
                    keys: [
                        { key: "background", title: "Input Background", path: ["input", "style"] },
                        { key: "size", excluded: ["height"] },
                        { key: "spacing", title: "Input Spacing", path: ["input", "style"], excluded: ["margin"] },
                        { key: "borders", title: "Input Borders", path: ["input", "style"] },
                        { key: "background", title: "Result Background", path: ["resultStyle"] },
                        { key: "typography", title: "Result Typography", path: ["resultStyle"] },
                        { key: "borders", title: "Result Borders", path: ["resultStyle"] },
                        { key: "styleIcon", title: "Search Icon", path: ["icons", "search", "style"], excluded: ["backgroundColor"] },
                        {
                            key: "styleIcon",
                            title: "Product Icon",
                            path: ["icons", "products", "style"],
                            excluded: ["backgroundColor"]
                        },
                        {
                            key: "styleIcon",
                            title: "Customer Icon",
                            path: ["icons", "customers", "style"],
                            excluded: ["backgroundColor"]
                        },
                        { key: "styleIcon", title: "Order Icon", path: ["icons", "orders", "style"], excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.FOCUS, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "search",
            type: "component",
            included: {
                products: true,
                customers: true,
                orders: true
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        } as SearchNode
    },
    menu: {
        ui: {
            key: "menu",
            label: "Menu",
            icon: "/menu-element-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [{ key: "editMenu" }]
                },
                style: {
                    keys: [
                        { key: "layout" },
                        { key: "background", title: "Background" },
                        { key: "size" },
                        { key: "spacing" },
                        { key: "layout", title: "Menu element layout", path: ["menu", "itemLayout"], excluded: ["alignItems", "justifyContent"] },
                        // { key: "typography", title: "Link text styling", path: ["menu", "linkStyle"] },
                        { key: "styleMenuTypography", title: "Typography" },
                        // { key: "styleIcon", title: "Icon styling", path: ["menu", "iconStyle"], excluded: ["backgroundColor", "rotate"] },
                        { key: "styleMenuIcon", title: "Icons" }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.SELECTED, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "menu",
            type: "component",
            menu: {
                links: [
                    {
                        name: "Main",
                        id: uuidv4(),
                        link: "0",
                        icon: {
                            src: "store",
                            style: {
                                transform: "rotate(0deg)"
                            }
                        }
                    },

                    {
                        name: "Link 1",
                        id: uuidv4(),
                        link: "",
                        icon: {
                            src: "link",
                            style: {
                                transform: "rotate(0deg)"
                            }
                        }
                    },

                    {
                        name: "Link 2",
                        id: uuidv4(),
                        link: "",
                        icon: {
                            src: "link",
                            style: {
                                transform: "rotate(0deg)"
                            }
                        }
                    },
                    {
                        name: "Link 3",
                        id: uuidv4(),
                        link: "",
                        icon: {
                            src: "link",
                            style: {
                                transform: "rotate(0deg)"
                            }
                        }
                    }
                ],
                itemLayout: {
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "5px"
                },
                linkStyle: {
                    fontWeight: "400",
                    color: "#3d4c66",
                    fontSize: "14px"
                }
            },
            attr: {
                style: {
                    fontFamily: "inherit",
                    flexDirection: "row",
                    gap: "20px"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as MenuNode
    },
    homeMenuLink: {
        ui: {
            key: "homeMenuLink",
            label: "Home menu link",
            icon: "/home-link-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [{ key: "editIcon", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "background" },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "hideShow", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "homeMenuLink",
            type: "component",
            icon: {
                src: "home",
                colors: ["#ffffff"]
            },
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as IconNode
    },

    cartSection: {
        ui: {
            key: "cartSection",
            label: "Cart Section",
            icon: "/div-icon.svg",
            excludedChildren: [],
            group: "Main POS Elements",
            edit: {
                editor: { keys: [{ key: "sectionEdit", excluded: [] }] },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "cartSection",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL }
        }
    },
    //this is actually "slideout cart" - slideoutCart
    simpleCart: {
        ui: {
            key: "simpleCart",
            label: "Slideout Cart",
            icon: "/slideout-cart-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "simpleCart", excluded: [] },
                        {
                            key: "editIcon",
                            path: ["expandedNode", "openCartIcon"],
                            title: "Open cart icon",
                            excluded: ["actions"],
                            condition: (node, comparator) => node.id !== comparator
                        },
                        { key: "editCustomerCart", excluded: [] },

                        { key: "editLineItemsCart", excluded: [] },
                        { key: "editOrderNotesCart", excluded: [] },
                        {
                            key: "editIcon",
                            path: ["expandedNode", "icon"],
                            title: "Cart Icon",
                            excluded: ["actions"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "editIcon",
                            path: ["expandedNode", "closeIcon"],
                            title: "Close Cart Icon",
                            excluded: ["actions"],
                            condition: (node, comparator) => node.id === comparator
                        }
                    ]
                },
                style: {
                    keys: [
                        { key: "styleSimpleCart", excluded: ["height"], condition: (node, comparator) => node.id === comparator },
                        {
                            key: "layout",
                            excluded: ["flexDirection", "alignItems", "justifyContent"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        { key: "reverseRow", excluded: [] },
                        { key: "borders", excluded: [], condition: (node, comparator) => node.id !== comparator },
                        {
                            key: "typography",
                            title: "Open cart typography",
                            path: ["expandedNode", "typography", "openCart"],
                            excluded: ["textAlign"],
                            condition: (node, comparator) => node.id !== comparator
                        },
                        {
                            key: "typography",
                            title: "Title typography",
                            path: ["expandedNode", "typography", "cartName"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "customerIcon", "style"],
                            title: "Active Customer Icon"
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "customerXIcon", "style"],
                            title: "Remove Customer Icon"
                        },
                        {
                            key: "paymentGridButton",
                            title: "Active customer chip styling",
                            excluded: ["typography"],
                            path: ["expandedNode", "customerChip"]
                        },
                        {
                            key: "typography",
                            title: "Active customer typography",
                            path: ["expandedNode", "typography", "customer"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Product typography",
                            path: ["expandedNode", "typography", "product"],
                            excluded: ["textAlign"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "typography",
                            title: "Subtext typography",
                            path: ["expandedNode", "typography", "productSubtext"],
                            excluded: ["textAlign"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "typography",
                            title: "Order note label typography",
                            path: ["expandedNode", "typography", "orderNoteLabel"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Order note typography",
                            path: ["expandedNode", "typography", "orderNote"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Subtotal typography",
                            path: ["expandedNode", "typography", "subtotals"],
                            excluded: ["textAlign"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "typography",
                            title: "Summary subtext typography",
                            path: ["expandedNode", "typography", "summarySubtext"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Total typography",
                            path: ["expandedNode", "typography", "total"],
                            excluded: ["textAlign"],
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "openCartIcon", "style"],
                            title: "Open cart icon",
                            condition: (node, comparator) => node.id !== comparator
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "icon", "style"],
                            title: "Cart Icon",
                            condition: (node, comparator) => node.id === comparator
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "closeIcon", "style"],
                            title: "Close Cart Icon",
                            condition: (node, comparator) => node.id === comparator
                        }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "simpleCart",
            type: "simpleCart",
            preset: emptyFixedCartPreset,
            expandedNode: {
                name: "Cart",
                showTaxes: true,
                customizeTaxes: "Taxes",
                type: "component",
                id: "simpleCartExpanded",
                openMode: "overlayLeft",
                showCartDiscount: true,
                customizeCartDiscount: "Cart discount",
                showCartTotal: true,
                customizeCartTotal: "Total",
                showCartSubtotal: true,
                customizeCartSubtotal: "Subtotal",
                showItemUnit: true,
                balanceDueLabel: "Balance due",
                showBalance: true,
                showDeleteIcon: true,
                showNotes: true,
                showDiscountAndFees: true,
                showVariationDetails: true,
                showSku: true,
                typography: {
                    product: {
                        fontWeight: "500"
                    },
                    total: {
                        fontWeight: "500"
                    }
                },
                deleteItemIcon: {
                    src: "square-xmark",
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                },
                showActiveCustomer: true,
                customerIcon: {
                    src: "user-tick",
                    colors: ["blue"],
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                },
                customerXIcon: {
                    src: "user-tick",
                    colors: ["black"],
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        } as any
    },

    //this is actually "simple cart" - simpleCart
    unfixedCart: {
        ui: {
            key: "unfixedCart",
            label: "Simple Cart",
            icon: "/simple-cart-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editCustomerCart", excluded: [] },
                        { key: "editLineItemsCart", excluded: [] },
                        { key: "editOrderNotesCart", excluded: [] },
                        { key: "simpleCart", excluded: ["fixed"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "styleSimpleCart", excluded: [] },
                        { key: "borders", excluded: [] },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "customerIcon", "style"],
                            title: "Active Customer Icon"
                        },
                        {
                            key: "styleIcon",
                            excluded: ["backgroundColor"],
                            path: ["expandedNode", "customerXIcon", "style"],
                            title: "Remove Customer Icon"
                        },
                        {
                            key: "paymentGridButton",
                            title: "Active customer chip styling",
                            excluded: ["typography"],
                            path: ["expandedNode", "customerChip"]
                        },

                        {
                            key: "typography",
                            title: "Active customer typography",
                            path: ["expandedNode", "typography", "customer"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Customer typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: TextNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Customer typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: TextNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Customer typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: TextNode) => !!node?.fallback?.value
                        },
                        {
                            key: "typography",
                            title: "Product typography",
                            path: ["expandedNode", "typography", "product"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Subtext typography",
                            path: ["expandedNode", "typography", "productSubtext"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Order note label typography",
                            path: ["expandedNode", "typography", "orderNoteLabel"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Order note typography",
                            path: ["expandedNode", "typography", "orderNote"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Subtotal typography",
                            path: ["expandedNode", "typography", "subtotals"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Summary subtext typography",
                            path: ["expandedNode", "typography", "summarySubtext"],
                            excluded: ["textAlign"]
                        },
                        {
                            key: "typography",
                            title: "Total typography",
                            path: ["expandedNode", "typography", "total"],
                            excluded: ["textAlign"]
                        }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "unfixedCart",
            type: "simpleCart",
            preset: emptyUnfixedCartPreset,
            expandedNode: {
                name: "Cart",
                showTaxes: true,
                customizeTaxes: "Taxes",
                type: "component",
                // cartWidth: "307px",
                // cartHeight: "600px",
                id: "simpleCartExpanded",
                showCartDiscount: true,
                customizeCartDiscount: "Cart discount",
                showCartTotal: true,
                customizeCartTotal: "Total",
                showCartSubtotal: true,
                customizeCartSubtotal: "Subtotal",
                showBalance: true,
                balanceDueLabel: "Balance due",
                showItemUnit: true,
                typography: {
                    product: {
                        fontWeight: "500"
                    },
                    total: {
                        fontWeight: "500"
                    },
                    subtotals: {
                        fontWeight: "500"
                    }
                },
                deleteItemIcon: {
                    src: "square-xmark",
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                },
                showActiveCustomer: true,
                customerIcon: {
                    src: "user-tick",
                    colors: ["blue", "blue"],
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                },
                customerXIcon: {
                    src: "cross",
                    colors: ["black"],
                    style: {
                        width: "32px",
                        height: "32px"
                    }
                }
            },
            dynamicData: "customer.fullName",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as any
    },

    printButton: {
        ui: {
            key: "printButton",
            label: "Print Button",
            icon: "/print-button-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editText", excluded: [] },
                        { key: "printer", excluded: [] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "printButton",
            type: "component",
            text: "Print button",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PrintButtonNode
    },
    barcode: {
        ui: {
            key: "barcode",
            label: "Barcode",
            icon: "/barcode-icon.svg",
            excludedChildren: ["*"],
            group: "Main POS Elements",
            edit: {
                editor: {
                    keys: [{ key: "editBarcode", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "barcodeStyle", excluded: [] },
                        { key: "spacing", title: "Spacing", excluded: ["padding"] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT]
        },
        render: {
            id: "",
            name: "barcode",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    },

    // Product Elements
    productSection: {
        ui: {
            key: "productSection",
            label: "product section",
            icon: "/div-icon.svg",
            excludedChildren: [],
            group: "Product Elements",
            edit: {
                editor: {
                    keys: [{ key: "sectionEdit", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "productSection",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL }
        }
    },
    productGrid: {
        ui: {
            key: "productGrid",
            label: "Product Grid",
            icon: "/product-grid-icon.svg",
            excludedChildren: ["*"],
            group: "Product Elements",
            edit: {
                editor: {
                    keys: [{ key: "productQuery", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "styleGrid", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "productGrid",
            type: "repeater",
            preset: BasicProductGridPreset,
            query: {
                dataType: QueryDataTables.PRODUCTS,
                limit: 25,
                order: {
                    orderBy: QueryOrderByOptionsProducts.NAME,
                    orderDirection: "descending"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL },
            attr: {
                style: {
                    flexGrow: 1,
                    height: "0",
                    gridTemplateColumns: "repeat(4, 1fr)"
                }
            }
        } as DynamicGridNode
    },
    productList: {
        ui: {
            key: "productList",
            label: "Product List",
            icon: "/dynamic-list-icon.svg",
            excludedChildren: ["*"],
            group: "Product Elements",
            edit: {
                editor: {
                    keys: [{ key: "productQuery", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "styleGrid", title: "List", excluded: ["gridTemplateRows", "columnGap"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "productList",
            type: "repeater",
            preset: BasicProductListPreset,
            query: {
                dataType: QueryDataTables.PRODUCTS,
                limit: 25
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL },
            attr: {
                style: {
                    gridTemplateColumns: "repeat(1, 1fr)",
                    flexGrow: 1,
                    height: "0"
                }
            }
        } as DynamicGridNode
    },
    productQuantity: {
        ui: {
            key: "productQuantity",
            label: "Product Quantity",
            icon: "/product-quantity-icon.svg",
            excludedChildren: ["*"],
            group: "Product Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "productQuantity", excluded: [] },
                        { key: "editIcon", excluded: [], title: "Decrement icon", path: ["icons", "decrement"], noRemoveImage: true },
                        { key: "editIcon", excluded: [], title: "Increment icon", path: ["icons", "increment"], noRemoveImage: true }
                    ]
                },
                style: {
                    keys: [
                        { key: "background", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "typography", path: ["inputTypography"] },
                        {
                            key: "size",
                            title: "Input size",
                            path: ["inputTypography"],
                            excluded: ["height", "overflowX", "overflowY", "fit-content"]
                        },
                        { key: "background", title: "Button background", path: ["buttonStyle"] },
                        { key: "borders", title: "Button borders", path: ["buttonStyle"] },
                        { key: "spacing", title: "Button spacing", path: ["buttonStyle"], excluded: ["margin"] },
                        { key: "styleIcon", title: "Decrement icon", path: ["icons", "decrement", "style"], excluded: ["rotate"] },
                        { key: "styleIcon", title: "Increment icon", path: ["icons", "increment", "style"], excluded: ["rotate"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "productQuantity",
            type: "component",
            inputTypography: {
                fontWeight: "500"
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            icons: {
                decrement: {
                    src: "minus",
                    style: { width: "32px", height: "32px" }
                },
                increment: {
                    src: "add",
                    style: { width: "32px", height: "32px" }
                }
            }
        } as ProductQuantityNode
    },
    productVariations: {
        ui: {
            key: "productVariations",
            label: "Product Variations",
            icon: "/product-variations-icon.svg",
            excludedChildren: ["*"],
            group: "Product Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "variantOptions", excluded: [] },
                        { key: "editText", excluded: ["dynamic"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "variantGrid", excluded: [] },
                        { key: "variant" },
                        { key: "background" },
                        { key: "spacing" },
                        { key: "borders" },
                        { key: "typography", title: "Title Typography", path: ["titleStyles"] },
                        { key: "typography", title: "Group Name Typography", path: ["groupFontStyles"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.SELECTED, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "productVariations",
            type: "component",
            text: "Select variation",
            preset: noVariationsPreset,
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        } as ProductVariationsNode
    },

    // Order Elements
    orderSection: {
        ui: {
            key: "orderSection",
            label: "Order section",
            icon: "/div-icon.svg",
            excludedChildren: [],
            group: "Order Elements",
            edit: {
                editor: {
                    keys: [{ key: "sectionEdit", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "orderSection",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL }
        }
    },
    orderList: {
        ui: {
            key: "orderList",
            label: "Order List",
            icon: "/dynamic-list-icon.svg",
            excludedChildren: ["*"],
            group: "Order Elements",
            edit: {
                editor: {
                    keys: [{ key: "orderQuery", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "styleGrid", title: "List", excluded: ["gridTemplateRows", "columnGap"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        {
                            key: "typography",
                            title: "Divider typography",
                            path: ["dividerStyles"],
                            condition: (node: DynamicGridNode) => !!node?.groupBy
                        },
                        { key: "spacing", title: "Divider spacing", path: ["dividerStyles"], condition: (node: DynamicGridNode) => !!node?.groupBy },
                        { key: "borders", title: "Divider line", path: ["dividerStyles"], condition: (node: DynamicGridNode) => !!node?.groupBy }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "orderList",
            type: "repeater",
            preset: orderListPreset as BuildNode[],
            query: {
                dataType: QueryDataTables.ORDERS,
                limit: 25,
                queries: {
                    orderCustomer: "all",
                    orderOutlet: "all",
                    orderStatus: "all",
                    orderServedBy: "all",
                    orderSource: "all",
                    orderType: "all"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL },
            attr: {
                style: {
                    gridTemplateColumns: "repeat(1, 1fr)",
                    flexGrow: 1,
                    height: "0"
                }
            },
            dividerStyles: {
                borderColor: "#D9D1E0",
                borderStyle: "solid",
                borderWidth: "0px 0px 1px 0px",
                fontFamily: "inherit",
                fontSize: "16px",
                color: "#758696",
                padding: "8px 0px"
            }
        } as DynamicGridNode
    },
    orderData: {
        ui: {
            key: "orderData",
            label: "Order data",
            icon: "/receipt-data-icon.svg",
            excludedChildren: [],
            group: "Order Elements",
            edit: {
                style: {
                    keys: [
                        { key: "size", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "orderData",
            type: "component",
            preset: orderDataPreset as BuildNode[]
        }
    },
    orderRefunds: {
        ui: {
            key: "orderRefunds",
            label: "Order refunds",
            icon: "/receipt-data-icon.svg",
            excludedChildren: ["*"],
            group: "Order Elements",
            exclude: true,
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "orderRefunds",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.FIT },
            attr: {
                style: {
                    width: "583px",
                    maxWidth: "100%"
                }
            }
        }
    },

    // Customer Elements
    customerSection: {
        ui: {
            key: "customerSection",
            label: "Customer Section",
            icon: "/div-icon.svg",
            excludedChildren: [],
            group: "Customer Elements",
            edit: {
                editor: {
                    keys: [{ key: "sectionEdit", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customerSection",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL }
        }
    },
    customerList: {
        ui: {
            key: "customerList",
            label: "Customer List",
            icon: "/dynamic-list-icon.svg",
            excludedChildren: ["*"],
            group: "Customer Elements",
            edit: {
                editor: {
                    keys: [{ key: "customerQuery", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "styleGrid", title: "List", excluded: ["gridTemplateRows", "columnGap"] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        {
                            key: "typography",
                            title: "Divider typography",
                            path: ["dividerStyles"],
                            condition: (node: DynamicGridNode) => !!node?.groupBy
                        },
                        { key: "spacing", title: "Divider spacing", path: ["dividerStyles"], condition: (node: DynamicGridNode) => !!node?.groupBy },
                        { key: "borders", title: "Divider line", path: ["dividerStyles"], condition: (node: DynamicGridNode) => !!node?.groupBy }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customerList",
            type: "repeater",
            preset: BasicCustomerListPreset as BuildNode[]
        } as DynamicGridNode
    },
    customerNotes: {
        ui: {
            key: "customerNotes",
            label: "Customer Notes",
            icon: "/customer-notes.svg",
            excludedChildren: ["*"],
            group: "Customer Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editCustomerNotes", excluded: [] },
                        {
                            key: "editIcon",
                            excluded: [],
                            condition: (n: any) => n.addNoteButton,
                            title: "Button icon",
                            path: ["addNoteButton", "icon"]
                        },
                        {
                            key: "editIcon",
                            excluded: [],
                            condition: (n: any) => n.deleteNoteIcon,
                            title: "Delete note icon",
                            path: ["deleteNoteIcon"],
                            noRemoveImage: true
                        }
                    ]
                },
                style: {
                    keys: [
                        { key: "styleContainer", excluded: [], title: "Container" },
                        { key: "typography", path: ["title", "style"], title: "Title typography" },
                        { key: "styleFullButton", path: ["addNoteButton", "style"], title: "Button", condition: (n: any) => n.addNoteButton },
                        {
                            key: "styleIcon",
                            path: ["addNoteButton", "icon", "style"],
                            title: "Button icon",
                            condition: (n: any) => n.addNoteButton?.icon?.src
                        },
                        { key: "background", path: ["dividerStyle"], title: "Divider color" },
                        { key: "typography", path: ["dateText", "style"], title: "Date typography" },
                        { key: "typography", path: ["timeText", "style"], title: "Time typography" },
                        { key: "typography", path: ["noteStyle"], title: "Note typography" },
                        { key: "styleIcon", path: ["deleteNoteIcon", "style"], title: "Delete icon", condition: (n: any) => n.deleteNoteIcon?.src }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT]
        },
        render: {
            id: "",
            name: "customerNotes",
            type: "component",
            preset: customerNotesPreset,
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        } as CustomerNotesNode
    },

    //Print Elements
    receiptData: {
        ui: {
            key: "receiptData",
            label: "Receipt Data",
            icon: "/receipt-data-icon.svg",
            excludedChildren: ["*"],
            group: "Print Elements",
            edit: {
                editor: { keys: [{ key: "receiptData", excluded: [] }] },
                style: {
                    keys: [
                        { key: "productTitles", excluded: [] },
                        { key: "productPrice", excluded: [] },
                        { key: "productSku", excluded: [] },
                        { key: "productVariations", excluded: [] },
                        { key: "productDescription", excluded: [] },
                        { key: "productDiscount", excluded: [] },
                        { key: "productTax", excluded: [] },
                        { key: "productTotal", excluded: [] },
                        { key: "productTotalPrice", excluded: [] },
                        { key: "cartTotal", excluded: [] },
                        { key: "cartPayment", excluded: [] },
                        { key: "cartEMV", excluded: [] },
                        { key: "typography", title: "Signature label", path: ["signature", "labelStyle"], excluded: ["color"] },
                        { key: "typography", title: "Signature subtext", path: ["signature", "subtextStyle"], excluded: ["color"] }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            },
            states: [NodeStates.DEFAULT]
        },
        render: {
            id: "",
            name: "receiptData",
            type: "component",
            styles: {},
            settings: {
                showValues: true,
                showVariations: true,
                showNotes: true,
                showSku: true,
                showDescription: true,
                showTips: true,
                showProductTaxes: true,
                showProductDiscount: true,
                showProductFee: true,
                showFee: true,
                showCartFee: true,
                showCartTaxes: true,
                showCartDiscount: true,
                showSignature: true
            },
            signature: {
                label: "Cardholder Signature:",
                subtext:
                    "I confirm that all goods/services have been received and agree to pay the above total according to the cardholder agreement."
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as ReceiptNode
    },
    cashLedgerData: {
        ui: {
            key: "cashLedgerData",
            label: "Cash Ledger Data",
            icon: "/cash-ledger-data-icon.svg",
            excludedChildren: ["*"],
            group: "Print Elements",
            edit: {
                editor: {
                    keys: [{ key: "cashLedgerData", excluded: [] }]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT]
        },
        render: {
            id: "",
            name: "cashLedgerData",
            type: "component",
            settings: {
                showTime: true
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as CashLedgerNode
    },
    eosData: {
        ui: {
            key: "eosData",
            label: "EOS Data",
            icon: "/eos-data-icon.svg",
            excludedChildren: ["*"],
            group: "Print Elements",
            edit: {
                editor: {
                    keys: [{ key: "eosData", excluded: [] }]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT]
        },
        render: {
            id: "",
            name: "eosData",
            type: "component",
            settings: {
                showSessionSummary: true,
                showCashDrawer: true,
                showFloatAdjustment: true,
                showClosingNotes: true,
                showPaymentBreakdown: true,
                showTaxBreakdown: true,
                showRefunds: true,
                showDiscount: true,
                showStaffBreakdown: true
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as EOSNode
    },
    //Payment Elements
    paymentGrid: {
        ui: {
            key: "paymentGrid",
            label: "Payment Grid",
            icon: "/payment-grid-icon.svg",
            excludedChildren: [],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [{ key: "paymentGrid", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "editPaymentGrid", excluded: ["flexDirection", "gap"] },
                        { key: "paymentGridButton", excluded: [], path: ["buttonStyles"] },
                        { key: "spacing", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "paymentGrid",
            type: "component",
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.CUSTOM }
        }
    },
    cashButton: {
        ui: {
            key: "cashButton",
            label: "Cash Button",
            icon: "/cash-button-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["connection", "custom"] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "cashButton",
            type: "component",
            text: "Cash Button",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PaymentButtonNode
    },
    tapToPay: {
        ui: {
            key: "tapToPay",
            label: "Final - TapToPay",
            icon: "/taptopay-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["cash", "connection", "custom"] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "tapToPay",
            type: "component",
            text: "Tap to pay",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PaymentButtonNode
    },
    terminalButton: {
        ui: {
            key: "terminalButton",
            label: "Final - Terminal",
            icon: "/terminal-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["cash", "custom"] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "terminalButton",
            type: "component",
            text: "Pay with terminal",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PaymentButtonNode
    },
    motoButton: {
        ui: {
            key: "motoButton",
            label: "Final - MOTO",
            icon: "/terminal-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["cash", "custom"] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "motoButton",
            type: "component",
            text: "MOTO Payment",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PaymentButtonNode
    },
    customPayment: {
        ui: {
            key: "customPayment",
            label: "Custom Payment",
            icon: "/custom-payment-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["cash", "connection"] },
                        { key: "editIcon", excluded: [] },
                        { key: "info", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customPayment",
            type: "component",
            text: "Custom payment",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as PaymentButtonNode
    },
    vendaraPayment: {
        ui: {
            key: "vendaraPayment",
            label: "Vendara payment",
            icon: "/vendara-icon.svg",
            excludedChildren: ["*"],
            group: "Payment Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "payment", excluded: ["cash", "custom"] },
                        { key: "editIcon", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: ["backgroundColor"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "vendaraPayment",
            type: "component",
            text: "Pay with Vendara",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT },
            attr: {
                style: {
                    borderWidth: "1px 1px 1px 1px",
                    borderStyle: "solid",
                    borderColor: "#7E57C5",
                    color: "#7E57C5",
                    backgroundColor: "#ffffff",
                    gap: "8px"
                }
            },
            icon: {
                src: "vendara"
            }
        } as PaymentButtonNode
    },
    //Filter Elements

    //searchbox
    searchField: {
        ui: {
            key: "searchField",
            label: "Searchbox",
            icon: "/search-field-icon.svg",
            excludedChildren: ["*"],
            group: "Filters",
            edit: {
                editor: {
                    keys: [
                        { key: "filterTarget", excluded: [] },
                        { key: "editSearchField", excluded: [] },
                        { key: "editIcon", title: "Search Icon", path: ["searchIcon"] },
                        { key: "editIcon", title: "Clear Icon", path: ["clearIcon"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "textInput", title: "Input", excluded: ["size", "borders", "background"], path: ["input", "style"] },
                        { key: "layout", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: ["fit", "scroll"] },
                        { key: "spacing", excluded: [] },
                        { key: "borders" },
                        { key: "typography", title: "Label", path: ["input", "labelStyle"] },
                        { key: "styleIcon", title: "Search Icon", path: ["searchIcon", "style"] },
                        { key: "styleIcon", title: "Clear Icon", path: ["clearIcon", "style"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.FOCUS, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "searchField",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column",
                    fontFamily: "inherit"
                }
            },
            input: {
                placeholder: "Search",
                style: {
                    fontFamily: "inherit"
                }
            },
            searchIcon: {
                src: "search 2",
                colors: ["#758696"],
                style: {
                    width: "24px",
                    height: "24px"
                }
            },

            clearIcon: {
                src: "cross",
                colors: ["#758696"],
                style: {
                    width: "32px",
                    height: "32px"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as SearchFieldNode
    },
    dropdownField: {
        ui: {
            key: "dropdownField",
            label: "Dropdown Field",
            icon: "/dropdown-field-icon.svg",
            excludedChildren: ["*"],
            group: "Filters",
            edit: {
                editor: {
                    keys: [
                        { key: "filterTarget", excluded: [] },
                        { key: "editDropdownField", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "textInput", title: "Input", excluded: ["size"], path: ["input", "style"] },
                        { key: "textInput", title: "Option", path: ["dropdownOptionStyle"], excluded: ["sizeWidth"] },
                        { key: "layout", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: ["fit", "scroll"] },
                        { key: "spacing", excluded: [] },
                        { key: "typography", title: "Label Typography", path: ["input", "labelStyle"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.FOCUS, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "dropdownField",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column",
                    fontFamily: "inherit"
                }
            },
            input: {
                placeholder: "Dropdown",
                style: {
                    fontFamily: "inherit"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            filter: {
                typeOfFilter: QueryFieldsProducts.ONSALE
            }
        } as FilterNode
    },
    //tab filters
    radioField: {
        ui: {
            key: "radioField",
            label: "Tab Filters",
            icon: "/radio-filter-icon.svg",
            excludedChildren: ["*"],
            group: "Filters",
            edit: {
                editor: {
                    keys: [
                        { key: "filterTarget", excluded: [] },
                        { key: "editRadioField", excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", path: ["input", "style"], excluded: [] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "typography", title: "Label", path: ["input", "labelStyle"] },
                        { key: "textInput", title: "Radio Button", path: ["radioStyle"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.SELECTED, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "radioField",
            type: "component",
            input: {
                style: {
                    flexDirection: "row",
                    fontFamily: "inherit"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            filter: {
                typeOfFilter: QueryFieldsProducts.CATEGORIES
            }
        } as FilterNode
    },
    //Advanced Elements
    slideout: {
        ui: {
            key: "slideout",
            label: "Slide Out",
            icon: "/slideout-icon.svg",
            excludedChildren: [],
            group: "Advanced Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "editSlideout", excluded: [] },
                        { key: "editIcon", path: ["slideout", "header", "icon"], title: "Header Icon", excluded: ["actions"] },
                        {
                            key: "editIcon",
                            path: ["slideout", "header", "closeButton", "icon"],
                            title: "Close Button Icon",
                            excluded: ["actions"]
                        }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: [] },
                        { key: "background", excluded: [] },
                        { key: "size", excluded: ["height"] },
                        { key: "spacing", excluded: ["margin"] },
                        { key: "borders", excluded: [] },
                        { key: "background", title: "Header Background", path: ["slideout", "header", "style"] },
                        { key: "typography", title: "Header Typography", path: ["slideout", "header", "title", "style"] },
                        { key: "spacing", title: "Header Spacing", path: ["slideout", "header", "style"], excluded: ["margin"] },
                        { key: "borders", title: "Header Borders", path: ["slideout", "header", "style"] },
                        { key: "styleIcon", title: "Header Icon", path: ["slideout", "header", "icon", "style"] },
                        { key: "styleIcon", title: "Close Button Icon", path: ["slideout", "header", "closeButton", "icon", "style"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "slideout",
            type: "component",
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            slideout: {
                openMode: "overlayLeft"
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        } as SlideoutNode
    },
    embedSection: {
        ui: {
            key: "embedSection",
            label: "Embed Section",
            icon: "/embed-section-icon.svg",
            excludedChildren: ["*"],
            group: "Advanced Elements",
            edit: {
                editor: {
                    keys: [{ key: "embedSection", excluded: [] }]
                },
                style: {
                    keys: [{ key: "background" }, { key: "size" }, { key: "spacing", excluded: ["padding"] }, { key: "borders" }]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "embedSection",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    },
    iframe: {
        ui: {
            key: "iframe",
            label: "Iframe",
            icon: "/iframe-icon.svg",
            excludedChildren: ["*"],
            group: "Advanced Elements",
            edit: {
                editor: {
                    keys: [{ key: "editIframe", excluded: [] }]
                },
                style: {
                    keys: [{ key: "size", excluded: ["fit-content", "overflowX", "overflowY"] }, { key: "spacing" }, { key: "borders" }]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.DISABLED]
        },

        render: {
            id: "",
            name: "iframe",
            type: "component",
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.FILL },
            attr: {
                style: {
                    flexGrow: 1,
                    height: "0"
                }
            }
        }
    },
    //Custom sale:
    customSale: {
        ui: {
            key: "customSale",
            label: "Custom Sale",
            icon: "/custom-sale-icon.svg",
            excludedChildren: [],
            group: "Advanced Elements",
            edit: {
                style: {
                    keys: [
                        { key: "layout", excluded: ["reverseRow"] },
                        { key: "background", excluded: [] },
                        { key: "backgroundImage" },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customSale",
            type: "component",
            preset: customSalePreset as BuildNode[],
            attr: {
                style: {
                    flexDirection: "column"
                }
            },
            nodeSize: { width: NodeSizes.FILL, height: NodeSizes.CUSTOM }
        }
    },
    vismaElement: {
        ui: {
            key: "vismaElement",
            label: "Visma Element",
            icon: "/clock.svg",
            excludedChildren: ["*"],
            group: "Advanced Elements",
            edit: {
                style: {
                    keys: [
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            extension: ExtensionGUID.TIME_M_SYSTEM,
            states: []
        },
        render: {
            id: "",
            name: "vismaElement",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            preset: vismaElementPreset as BuildNode[]
        }
    },

    //Visma Elements
    vismaPage: {
        ui: {
            key: "vismaPage",
            label: "Visma Page",
            icon: "/clock.svg",
            excludedChildren: ["*"],
            group: "Visma?",
            edit: {
                style: {
                    keys: [{ key: "size", excluded: [] }]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [],
            exclude: true
        },
        render: {
            id: "",
            name: "vismaPage",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    },
    vismaButton: {
        ui: {
            key: "vismaButton",
            label: "Button",
            icon: "/button-icon.svg",
            excludedChildren: ["*"],
            group: "Visma?",
            exclude: true,
            edit: {
                editor: {
                    keys: [
                        { key: "button", excluded: [] },
                        { key: "editIcon", excluded: ["actions"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        {
                            key: "typography",
                            title: "Typography (Prepend)",
                            path: ["prepend", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.prepend?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Append)",
                            path: ["append", "style"],
                            excluded: ["textAlign"],
                            condition: (node: ButtonNode) => !!node?.append?.value
                        },
                        {
                            key: "typography",
                            title: "Typography (Fallback)",
                            path: ["fallback", "style"],
                            excluded: [],
                            condition: (node: ButtonNode) => !!node?.fallback?.value
                        },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "dynamicDataOptions", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "vismaButton",
            type: "component",
            text: "Click me",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        }
    },
    vismaInput: {
        ui: {
            key: "vismaInput",
            label: "Visma Input",
            icon: "/clock.svg",
            excludedChildren: ["*"],
            group: "Visma?",
            edit: {
                editor: {
                    keys: [{ key: "search", excluded: [] }]
                },
                style: {
                    keys: [
                        { key: "background", title: "Input Background", path: ["input", "style"] },
                        { key: "size", excluded: ["height"] },
                        { key: "spacing", title: "Input Spacing", path: ["input", "style"], excluded: ["margin"] },
                        { key: "borders", title: "Input Borders", path: ["input", "style"] },
                        { key: "typography", title: "Input Typography", path: ["input", "style"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.FOCUS, NodeStates.DISABLED],
            exclude: true
        },
        render: {
            id: "",
            name: "vismaInput",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        }
    },
    vismaButtonList: {
        ui: {
            key: "vismaButtonList",
            label: "Button List",
            icon: "/clock.svg",
            excludedChildren: ["*"],
            group: "Visma?",
            edit: {
                style: {
                    keys: [
                        { key: "size" },
                        { key: "background", title: "Button Background", path: ["buttonStyle"] },
                        { key: "typography", title: "Button Typography", path: ["buttonStyle"] },
                        { key: "spacing", title: "Button Spacing", path: ["buttonStyle"] },
                        { key: "borders", title: "Button Borders", path: ["buttonStyle"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED],
            exclude: true
        },
        render: {
            id: "",
            name: "vismaButtonList",
            type: "component",
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            attr: {
                style: {
                    width: "100%"
                }
            },
            buttonStyle: {
                width: "100%",
                fontWeight: "400",
                fontSize: "32px",
                color: "#ffffff",
                backgroundColor: "#2797e8",
                borderRadius: "8px",
                border: "none",
                padding: "10px 20px"
            }
        } as VismaButtonListNode
    },

    customNumpad: {
        ui: {
            key: "customNumpad",
            label: "Custom Numpad",
            icon: "/numpad-icon.svg",
            excludedChildren: [],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            exclude: true,
            edit: {
                editor: { keys: [{ key: "chooseLinkedInputTarget", excluded: [] }] },
                style: {
                    keys: [
                        { key: "size", excluded: [] },
                        {
                            key: "numpadGrid",
                            title: "Numpad Layout",
                            excluded: []
                        },
                        { key: "background", excluded: [] },
                        { key: "spacing", excluded: ["margin"] },
                        { key: "borders" },
                        {
                            key: "typography",
                            title: "Key Typography",
                            path: ["keyStyle"],
                            excluded: []
                        },
                        {
                            key: "size",
                            title: "Key Size",
                            path: ["keyStyle"],
                            excluded: []
                        },
                        { key: "background", title: "Key Background", path: ["keyStyle"] },

                        {
                            key: "spacing",
                            title: "Key Spacing",
                            path: ["keyStyle"],
                            excluded: []
                        },
                        {
                            key: "borders",
                            title: "Key Borders",
                            path: ["keyStyle"],
                            excluded: []
                        },

                        {
                            key: "editIcon",
                            title: "Delete Button Icon",
                            path: ["deleteIcon"],
                            excluded: []
                        },
                        {
                            key: "styleIcon",
                            path: ["deleteIcon", "style"],
                            title: "Delete Icon Style",
                            condition: (node: any) => !!node.deleteIcon?.src
                        }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customNumpad",
            type: "component",
            keyStyle: {
                width: "60px",
                height: "60px",
                backgroundColor: "#ffffff",
                color: "#243249",
                fontFamily: "inherit",
                fontSize: "18px",
                fontWeight: "400",
                border: "1px solid #e5e0eb",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            },
            deleteIcon: {
                src: "Tag-cross",
                style: {
                    width: "24px",
                    height: "24px"
                },
                colors: ["#243249"]
            },
            gridConfig: {
                columns: 3
            },
            attr: {
                style: {
                    height: "fit-content",
                    width: "fit-content",
                    padding: "0"
                }
            },
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        } as CustomNumpadNode
    },
    linkedInputDisplay: {
        ui: {
            key: "linkedInputDisplay",
            label: "Linked Input Display",
            icon: "/linked-input-display-icon.svg",
            exclude: true,
            excludedChildren: [],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            edit: {
                style: {
                    keys: [
                        { key: "layout", title: "Layout" },
                        { key: "typography", title: "Price typography", path: ["priceStyle"] },
                        { key: "typography", title: "Quantity typography", path: ["quantityStyle"], excluded: [] }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            }
        },
        render: {
            id: "",
            name: "linkedInputDisplay",
            type: "component",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        }
    },
    editableLabel: {
        ui: {
            key: "editableLabel",
            label: "Editable Label",
            icon: "/editable-label-icon.svg",
            exclude: true,
            excludedChildren: [],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED],
            edit: {
                editor: {
                    keys: [
                        { key: "editText", excluded: [] },
                        { key: "editIcon", title: "Icon", path: ["icon"], excluded: [] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", title: "Layout" },
                        { key: "reverseRow", title: "Reverse row" },
                        { key: "typography", title: "Label typography" },
                        { key: "styleIcon", path: ["icon", "style"], condition: (node: any) => !!node.icon?.src }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            }
        },
        render: {
            id: "",
            name: "editableLabel",
            type: "component",
            text: "Editable Label",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT },
            icon: {
                src: "Pen-Edit",
                style: {
                    width: "24px",
                    height: "24px"
                },
                colors: ["#2797E8"]
            },
            attr: {
                style: {
                    height: "fit-content",
                    width: "fit-content",
                    display: "flex",
                    flexDirection: "row",
                    padding: "0"
                }
            }
        } as ButtonNode
    },
    customSaleAdjustQuantity: {
        ui: {
            key: "customSaleAdjustQuantity",
            label: "Adjust Quantity",
            icon: "/product-quantity-icon.svg",
            exclude: true,
            excludedChildren: ["*"],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [
                        { key: "chooseLinkedInputTarget", excluded: [] },
                        { key: "editIcon", excluded: [], title: "Decrement icon", path: ["icons", "decrement"], noRemoveImage: true },
                        { key: "editIcon", excluded: [], title: "Increment icon", path: ["icons", "increment"], noRemoveImage: true }
                    ]
                },
                style: {
                    keys: [
                        { key: "background", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "typography", path: ["inputTypography"] },
                        {
                            key: "size",
                            title: "Input size",
                            path: ["inputTypography"],
                            excluded: ["height", "overflowX", "overflowY", "fit-content"]
                        },
                        { key: "background", title: "Button background", path: ["buttonStyle"] },
                        { key: "borders", title: "Button borders", path: ["buttonStyle"] },
                        { key: "spacing", title: "Button spacing", path: ["buttonStyle"], excluded: ["margin"] },
                        { key: "styleIcon", title: "Decrement icon", path: ["icons", "decrement", "style"], excluded: ["rotate"] },
                        { key: "styleIcon", title: "Increment icon", path: ["icons", "increment", "style"], excluded: ["rotate"] }
                    ]
                },
                advanced: {
                    keys: [{ key: "advanced", excluded: [] }]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customSaleAdjustQuantity",
            type: "component",
            inputTypography: {
                fontWeight: "500"
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM },
            icons: {
                decrement: {
                    src: "minus",
                    style: { width: "32px", height: "32px" }
                },
                increment: {
                    src: "add",
                    style: { width: "32px", height: "32px" }
                }
            }
        } as ProductQuantityNode
    },
    customSaleTaxConfiguration: {
        ui: {
            key: "customSaleTaxConfiguration",
            label: "Tax Configuration",
            icon: "/tax-configuration-icon.svg",
            exclude: true,
            excludedChildren: [],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            edit: {
                editor: { keys: [{ key: "editTaxConfigurations", excluded: [] }] },
                style: {
                    keys: [
                        { key: "layout", excluded: [] },
                        { key: "size", excluded: ["fit", "scroll"] },
                        { key: "spacing", excluded: [] },
                        { key: "borders" },
                        { key: "typography", title: "Label Typography", path: ["input", "labelStyle"] },
                        { key: "background", title: "Toggle color", path: ["input", "toggleStyle"] },
                        { key: "size", excluded: ["fit", "fill", "scroll"], path: ["input", "toggleStyle"], title: "Toggle size" },
                        { key: "textInput", title: "Input", excluded: ["size"], path: ["input", "style"] },
                        { key: "textInput", title: "Option", path: ["dropdownOptionStyle"], excluded: ["sizeWidth"] },
                        { key: "background", excluded: [] }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.FOCUS, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customSaleTaxConfiguration",
            type: "component",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT },
            attr: {
                style: {
                    fontFamily: "inherit"
                }
            }
        }
    },
    customSaleAddToCartButton: {
        ui: {
            key: "customSaleAddToCartButton",
            label: "Add to Cart Button",
            icon: "/simple-cart-icon.svg",
            exclude: true,
            excludedChildren: ["*"],
            restrictToParents: ["customSale"],
            group: "Design Elements",
            edit: {
                editor: {
                    keys: [
                        // { key: "chooseLinkedInputTarget", excluded: [] },
                        { key: "button", excluded: [] },
                        { key: "editAction", title: "Extra action" },
                        { key: "editIcon", excluded: ["actions"] }
                    ]
                },
                style: {
                    keys: [
                        { key: "layout", excluded: ["justifyContent", "alignItems"] },
                        { key: "background", excluded: [] },
                        { key: "typography", path: ["attr", "style"] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] },
                        { key: "borders", excluded: [] },
                        { key: "styleIcon", excluded: [] }
                    ]
                },
                advanced: {
                    keys: [
                        { key: "advanced", excluded: [] },
                        { key: "conditions", excluded: [] }
                    ]
                }
            },
            states: [NodeStates.DEFAULT, NodeStates.HOVER, NodeStates.ACTIVE, NodeStates.DISABLED]
        },
        render: {
            id: "",
            name: "customSaleAddToCartButton",
            type: "component",
            text: "Add to cart",
            nodeSize: { width: NodeSizes.FIT, height: NodeSizes.FIT }
        }
    },
    divider: {
        ui: {
            key: "divider",
            label: "Divider Line",
            icon: "/divider-line-icon.svg",
            excludedChildren: ["*"],
            group: "Design Elements",
            edit: {
                editor: { keys: [{ key: "lineStyling", excluded: ["color", "patternDirection", "gap"] }] },
                style: {
                    keys: [
                        { key: "lineStyling", excluded: ["type"] },
                        { key: "size", excluded: [] },
                        { key: "spacing", excluded: [] }
                    ]
                },
                advanced: { keys: [{ key: "advanced", excluded: [] }] }
            }
        },
        render: {
            id: "",
            name: "divider",
            type: "component",
            attr: {
                style: {
                    height: "10px"
                }
            },
            nodeSize: { width: NodeSizes.CUSTOM, height: NodeSizes.CUSTOM }
        }
    }
};

const typeMap: { [key in GlobalBlockTypes]: (keyof BuilderMap)[] } = {
    [GlobalBlockTypes.GB_PRINT]: ["div", "text", "icon", "image", "receiptData", "barcode", "divider", "orderSection"],
    [GlobalBlockTypes.GB_CFD]: [],
    [GlobalBlockTypes.GB_CI]: [],
    [GlobalBlockTypes.GB_GRID_CARD]: ["div", "text", "image", "icon", "divider"]
};

// const removedElems = [];

const checkSpecialCases = (key: string, company?: ActiveCompany, baseLayerType?: BaseLayerTypes) => {
    if (company) {
        switch (key) {
            case "vendaraPayment":
                if (!company.settings?.vendaraApiKey) {
                    return false;
                }
        }
    }

    if (baseLayerType === BaseLayerTypes.CFDS) {
        if (!validCFDElements.includes(key)) {
            return false;
        }
    }

    if (key === "search") return false;

    return true;
};

export const getAllUI = (
    selectedNode: string = "",
    gbType?: GlobalBlockTypes,
    buildType?: BuildTypes,
    company?: ActiveCompany,
    baseLayerType?: BaseLayerTypes
) => {
    const allUI: { [key: string]: BuilderUI[] } = {};
    Object.keys(builderMap).forEach(key => {
        // if (removedElems.includes(key)) return;
        if (!checkSpecialCases(key, company, baseLayerType)) return;
        if (["receiptData", "cashLedgerData", "eosData"].includes(key) && gbType !== "globalBlockPrint") return;
        if (key === "templateDiv" && buildType !== BuildTypes.OLIVER_TEMPLATE) return;
        if (key === "smartTile") return;
        if (!selectedNode || !builderMap[selectedNode as BuildComponentKey].ui.excludedChildren.includes(key)) {
            if (!gbType || typeMap[gbType].includes(key as BuildComponentKey)) {
                if (builderMap[key as BuildComponentKey].ui.exclude) return;
                if (builderMap[key as BuildComponentKey].ui.group) {
                    if (allUI[builderMap[key as BuildComponentKey].ui.group]) {
                        allUI[builderMap[key as BuildComponentKey].ui.group].push(builderMap[key as BuildComponentKey].ui);
                    } else {
                        allUI[builderMap[key as BuildComponentKey].ui.group] = [builderMap[key as BuildComponentKey].ui];
                    }
                }
            }
        }
    });
    return allUI;
};

export const getUIComponent = (key: BuildComponentKey): BuilderUI => {
    return builderMap[key].ui;
};

export const getComponent = (key: BuildComponentKey, parent?: string, children?: string[]): RenderBuildNode => {
    const component = { ...builderMap[key].render };
    component.id = uuidv4();
    if (parent) component.parent = parent;
    if (children) component.children = children;
    const componentClone = deepCopy(component);
    return componentClone;
};

export const noAscend = ["baseLayerPage", "baseLayerPopup", "baseLayerCFD", "globalBlockPrint", "globalBlockCFD", "globalBlockGridCard"];
export const reloadable = ["productGrid", "productList"];

export const isDeletable = (node: BuildNode): boolean => {
    const noDelete = ["baseLayerPage", "baseLayerPopup", "baseLayerCFD", "globalBlockPrint", "globalBlockCFD", "globalBlockGridCard", "repeaterDiv"];
    return !noDelete.includes(node.name) && !node.noDelete;
};

export const isCopyable = (node: BuildNode): boolean => {
    const noCopy = ["baseLayerPage", "baseLayerPopup", "baseLayerCFD", "globalBlockPrint", "globalBlockCFD", "globalBlockGridCard", "repeaterDiv"];
    return !noCopy.includes(node.name) && !node.noCopy;
};

export const isMovable = (node: BuildNode): boolean => {
    const noMove = ["baseLayerPage", "baseLayerPopup", "baseLayerCFD", "globalBlockPrint", "globalBlockCFD", "globalBlockGridCard", "repeaterDiv"];
    return !noMove.includes(node.name) && !node.noMove;
};

export const validRepeaterElements = ["div", "text", "button", "icon", "image", "divider"];
export const validCFDElements = ["div", "text", "button", "icon", "image", "unfixedCart", "divider", "orderSection", "customerSection"];
export const noFilterIcons = ["/vendara-icon.svg"];
export const containers = [
    "div",
    "repeaterDiv",
    "slideout",
    "orderData",
    "orderRefunds",
    "orderList",
    "templateDiv",
    "baseLayerPage",
    "baseLayerPopup",
    "baseLayerCFD",
    "globalBlockPrint",
    "productGrid",
    "productList",
    "productVariations",
    "customerNotes",
    "unfixedCart",
    "simpleCart",
    "cartSection",
    "productSection",
    "orderSection",
    "customerSection",
    "customerList",
    "vismaElement",
    "vismaPage",
    "customSale",
    "smartGrid",
    "smartTile"
];
